// src/Services/joinRoomService.js
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  limit,
} from 'firebase/firestore';
import { uniquePlayerName } from '../utils/uniquePlayerName';
import { db } from '../firebase';
import authService from './authService';
import analytics from './analyticsService';

class JoinRoomService {
  /**
   * Find room by room ID
   */
  async findRoom(roomId) {
    try {
      const cleanRoomId = (roomId || '').trim();
      const roomsRef = collection(db, 'rooms');

      // Try the short, human-friendly join code first (new rooms only — see
      // roomCreationService.js's generateShortCode). Normalized to
      // uppercase since that's how it's generated/displayed; this is a
      // SEPARATE field from roomId, so uppercasing here can never break the
      // roomId lookup below, which must keep using the raw input exactly as
      // typed (roomId is a mixed-case base36 string, not case-insensitive).
      // Mirrors the mobile app's services/joinRoomService.ts 1:1.
      const shortCodeQuery = query(
        roomsRef,
        where('shortCode', '==', cleanRoomId.toUpperCase()),
        where('isActive', '==', true),
        limit(1)
      );
      const shortCodeSnapshot = await getDocs(shortCodeQuery);
      if (!shortCodeSnapshot.empty) {
        const roomDoc = shortCodeSnapshot.docs[0];
        return {
          success: true,
          roomData: { id: roomDoc.id, ...roomDoc.data() },
          message: 'Room found successfully',
        };
      }

      const q = query(
        roomsRef,
        where('roomId', '==', cleanRoomId),
        where('isActive', '==', true),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return {
          success: false,
          error: 'room_not_found',
          message: 'Room not found. Please check the room code and try again.',
        };
      }

      const roomDoc = querySnapshot.docs[0];
      const roomData = {
        id: roomDoc.id,
        ...roomDoc.data(),
      };

      return {
        success: true,
        roomData,
        message: 'Room found successfully',
      };
    } catch (error) {
      console.error('Error finding room:', error);
      return {
        success: false,
        error: 'room_lookup_failed',
        code: error.code,
        message: 'Failed to find room. Please check your connection and try again.',
      };
    }
  }

  /**
   * Check if user is already in a room
   */
  async isUserInRoom(roomId) {
    try {
      const userRoomStatus = await this.getUserRoomStatus(roomId);
      if (userRoomStatus.lookupFailed) {
        throw new Error(userRoomStatus.code || userRoomStatus.error);
      }
      return userRoomStatus.isInRoom;
    } catch (error) {
      console.warn('Error checking user room status:', error);
      // Fallback to localStorage check
      try {
        const storedRoomId = localStorage.getItem('currentRoomId');
        const userId = localStorage.getItem('currentUserId');
        return storedRoomId === roomId && !!userId;
      } catch (fallbackError) {
        console.warn('Fallback localStorage check also failed:', fallbackError);
        return false;
      }
    }
  }

  /**
   * Get comprehensive user room status - checks Firebase and syncs local storage
   */
  async getUserRoomStatus(roomId) {
    try {
      // Viewing a room's join link doesn't create an account; joining does.
      const userId = await authService.getExistingUserId();

      // Find the room in Firebase
      const findResult = await this.findRoom(roomId);
      if (!findResult.success || !findResult.roomData) {
        return {
          isInRoom: false,
          isHost: false,
          // A missing room is a real answer; a failed lookup is not, so callers
          // must not treat it as "not in the room".
          lookupFailed: !findResult.success && findResult.error !== 'room_not_found',
          error: findResult.error,
          code: findResult.code,
          message: findResult.message,
        };
      }

      const roomData = findResult.roomData;

      // Check if user is in the room's player list
      const userPlayer = roomData.players?.find((player) => player.id === userId);

      if (userPlayer) {
        // User is in the room! Sync localStorage for performance. Store the
        // room's own canonical `roomId` field, not the raw `roomId` param —
        // that param can be a short join code, which every listener query
        // (`where('roomId', '==', ...)`) would never match. Mirrors mobile's
        // services/joinRoomService.ts 1:1.
        await this.syncLocalStorageFromFirebase(roomData.roomId, userId, userPlayer, roomData);

        return {
          isInRoom: true,
          isHost: userPlayer.isHost,
          playerName: userPlayer.name,
          roomData,
        };
      }

      // User is not in the room
      return {
        isInRoom: false,
        isHost: false,
        roomData,
      };
    } catch (error) {
      console.error('Error getting user room status:', error);
      return {
        isInRoom: false,
        isHost: false,
        lookupFailed: true,
        error: 'room_status_failed',
        code: error.code,
        message: 'Could not check your room status. Please check your connection and try again.',
      };
    }
  }

  /**
   * Sync localStorage with Firebase data for performance
   */
  async syncLocalStorageFromFirebase(roomId, userId, userPlayer, roomData) {
    try {
      localStorage.setItem('currentRoomId', roomId);
      localStorage.setItem('currentUserId', userId);
      localStorage.setItem('playerName', userPlayer.name);
      localStorage.setItem('isHost', userPlayer.isHost.toString());

      // Also update user's Firestore document if needed
      await authService.addRoomToUser(roomId, {
        roomName: roomData.roomName,
        isHost: userPlayer.isHost,
      });

      console.log('localStorage synced with Firebase data');
    } catch (error) {
      console.warn('Failed to sync localStorage:', error);
      // Not critical - the main functionality still works
    }
  }

  /**
   * Join a room with player name
   */
  async joinRoom(roomId, playerName, joinMethod = 'link') {
    if (!playerName.trim()) {
      return {
        success: false,
        message: 'Please enter your name to join the room.',
      };
    }

    try {
      // Check if user is already in this room
      const userRoomStatus = await this.getUserRoomStatus(roomId);

      if (userRoomStatus.lookupFailed) {
        // Can't tell whether the user is already in the room; joining now
        // could add them twice.
        analytics.track('room_join_failed', {
          reason: userRoomStatus.error,
          join_method: joinMethod,
        });
        return {
          success: false,
          error: userRoomStatus.error,
          code: userRoomStatus.code,
          message: userRoomStatus.message,
        };
      }

      if (userRoomStatus.isInRoom) {
        return {
          success: true,
          roomData: userRoomStatus.roomData,
          message: userRoomStatus.isHost
            ? `Welcome back! You're the host of ${userRoomStatus.roomData?.roomName}`
            : `Welcome back to ${userRoomStatus.roomData?.roomName}!`,
        };
      }

      // User is not in room, proceed with normal join flow. getUserRoomStatus
      // already found the room; reuse it so the "already in room" check and
      // the join act on the same snapshot.
      const roomData = userRoomStatus.roomData;
      if (!roomData) {
        const reason = userRoomStatus.error || 'room_not_found';
        analytics.track('room_join_failed', {
          reason,
          join_method: joinMethod,
        });
        return {
          success: false,
          error: reason,
          message: userRoomStatus.message,
        };
      }

      // Validate room status
      const validationResult = this.validateRoomForJoining(roomData);
      if (!validationResult.success) {
        analytics.track('room_join_failed', {
          reason: validationResult.error || validationResult.message || 'validation_failed',
          join_method: joinMethod,
        });
        return validationResult;
      }

      // Get Firebase user ID
      const userId = await authService.getCurrentUserId();

      // Two Semis can both play: the second becomes "Semi 2".
      const finalName = uniquePlayerName(playerName, roomData.players);

      // Create new player object
      const newPlayer = {
        id: userId,
        name: finalName,
        isHost: false,
        joinedAt: new Date(),
      };

      // Update room with new player
      const roomDocRef = doc(db, 'rooms', roomData.id);
      await updateDoc(roomDocRef, {
        players: arrayUnion(newPlayer),
      });

      // Update user documents and storage — use the room's own canonical
      // roomId, not the raw input, which may have been a short join code.
      await this.updateUserAfterJoining(userId, roomData.roomId, roomData.roomName, finalName);

      // Return updated room data
      const updatedRoomData = {
        ...roomData,
        players: [...roomData.players, newPlayer],
      };

      analytics.track('room_joined', {
        room_id: roomData.roomId,
        join_method: joinMethod,
        player_count_at_join: roomData.players.length,
      });

      return {
        success: true,
        roomData: updatedRoomData,
        message: `Successfully joined ${roomData.roomName}!`,
      };
    } catch (error) {
      console.error('Error joining room:', error);
      analytics.track('room_join_failed', {
        reason: error.code || error.message || 'unknown',
        join_method: joinMethod,
      });

      let errorMessage;
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. The room may be closed or expired.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else {
        errorMessage = error.message || 'Failed to join room. Please try again.';
      }

      return {
        success: false,
        error: error.code || 'unknown',
        message: errorMessage,
      };
    }
  }

  /**
   * Validate if room can be joined
   */
  validateRoomForJoining(roomData) {
    // Check if room is active
    if (!roomData.isActive) {
      return {
        success: false,
        error: 'room_inactive',
        message: 'This room is no longer active.',
      };
    }

    // Check if game has already started
    if (roomData.gameStatus === 'playing' || roomData.gameStatus === 'starting') {
      return {
        success: false,
        error: 'game_in_progress',
        message: 'Game is already in progress. You cannot join a game that has started.',
      };
    }

    // Check if game has ended
    if (roomData.gameStatus === 'ended') {
      return {
        success: false,
        error: 'game_ended',
        message: 'This game has already ended. You can view the results but cannot join.',
      };
    }

    // Check if room is full
    const currentPlayerCount = roomData.players?.length || 0;
    if (currentPlayerCount >= roomData.maxPlayers) {
      return {
        success: false,
        error: 'room_full',
        message: `Room is full (${currentPlayerCount}/${roomData.maxPlayers} players). Cannot join.`,
      };
    }

    // A name that's already taken isn't a rejection: joinRoom gives the
    // joiner "Name 2" instead (see uniquePlayerName).

    // Check if room has expired
    if (roomData.expiresAt && new Date() > new Date(roomData.expiresAt)) {
      return {
        success: false,
        error: 'room_expired',
        message: 'This room has expired.',
      };
    }

    return {
      success: true,
      message: 'Room is available to join',
    };
  }

  /**
   * Update user data after successfully joining
   */
  async updateUserAfterJoining(userId, roomId, roomName, playerName) {
    try {
      // Create user document and add room
      await authService.createUserDocument(userId);
      await authService.addRoomToUser(roomId, {
        roomName,
        isHost: false,
      });

      // Store in localStorage
      localStorage.setItem('currentRoomId', roomId);
      localStorage.setItem('currentUserId', userId);
      localStorage.setItem('playerName', playerName);
      localStorage.setItem('isHost', 'false');

      // Update joined rooms history
      try {
        const existingJoinedRoomsStr = localStorage.getItem('joinedRooms');
        const existingJoinedRooms = existingJoinedRoomsStr
          ? JSON.parse(existingJoinedRoomsStr)
          : [];

        const updatedJoinedRooms = [
          ...existingJoinedRooms,
          {
            roomId,
            roomName,
            joinedAt: Date.now(),
            joinedAs: playerName,
          },
        ].slice(-5); // Keep only last 5 rooms

        localStorage.setItem('joinedRooms', JSON.stringify(updatedJoinedRooms));
      } catch (localStorageError) {
        console.warn('localStorage update failed:', localStorageError);
      }

      // Handle recent rooms (including public room joins)
      await this.updateRecentRooms(roomId, roomName, playerName);
    } catch (error) {
      console.error('Error updating user after joining:', error);
      // Don't throw - joining was successful, this is just cleanup
    }
  }

  async updateRecentRooms(roomId, roomName, playerName) {
    try {
      // Check if this was a public room join
      const wasPublicJoin = localStorage.getItem('lastJoinWasPublic');
      const publicHostName = localStorage.getItem('publicRoomHostName');

      // Get existing recent rooms
      const existingRecentRoomsStr = localStorage.getItem('recentRooms');
      const existingRecentRooms = existingRecentRoomsStr ? JSON.parse(existingRecentRoomsStr) : [];

      // Create new recent room entry
      const newRecentRoom = {
        roomId,
        roomName,
        joinedAt: Date.now(),
        joinedAs: playerName,
        source: wasPublicJoin === 'true' ? 'public' : 'direct',
        ...(wasPublicJoin === 'true' && publicHostName && { hostName: publicHostName }),
      };

      // Add to front of recent rooms array and remove duplicates
      const updatedRecentRooms = [
        newRecentRoom,
        ...existingRecentRooms.filter((room) => room.roomId !== roomId),
      ].slice(0, 10); // Keep only last 10 recent rooms

      localStorage.setItem('recentRooms', JSON.stringify(updatedRecentRooms));

      // Clean up public room tracking flags
      if (wasPublicJoin === 'true') {
        localStorage.removeItem('lastJoinWasPublic');
        localStorage.removeItem('publicRoomHostName');
      }

      console.log(`Added room to recent rooms (source: ${newRecentRoom.source})`);
    } catch (error) {
      console.warn('Failed to update recent rooms:', error);
    }
  }
}

export const joinRoomService = new JoinRoomService();
export default joinRoomService;
